import { shallowEqual, useSelector } from 'react-redux';

import { AspectRatio } from 'js/src/client';
import type { RootState } from 'js/src/libs/RootState';

const TALLEST_FIRST: AspectRatio[] = [
    AspectRatio.VERTICAL,
    AspectRatio.FOURFIVE,
    AspectRatio.SQUARE,
    AspectRatio.LANDSCAPE,
];

export type TemplateRowSizing = {
    // The aspect ratio used to size grid columns. null means default (landscape).
    rowAspectRatio: AspectRatio | null;
    // True when the row contains more than one distinct aspect ratio. Callers use this
    // to force a uniform square container with object-fit: contain so heights line up.
    isMixed: boolean;
};

export function useTemplateRowSizing(
    templateIds: Iterable<string>,
    getAspectRatio: (state: RootState, id: string) => AspectRatio | null,
): TemplateRowSizing {
    return useSelector((state: RootState) => {
        let tallestIdx = TALLEST_FIRST.length - 1;
        const seen = new Set<AspectRatio>();
        for (const templateId of templateIds) {
            // Tiles render LANDSCAPE when default_aspect_ratio is null (see
            // TemplatePreviewButton.aspectRatioPadding), so the row-sizing logic must treat
            // null the same way — otherwise a row of [null, fourfive] is incorrectly
            // classified as uniform-fourfive and the null tiles render shorter.
            const ar = getAspectRatio(state, templateId) ?? AspectRatio.LANDSCAPE;
            seen.add(ar);
            const idx = TALLEST_FIRST.indexOf(ar);
            if (idx < tallestIdx) tallestIdx = idx;
        }
        const tallest = TALLEST_FIRST[tallestIdx];
        return {
            rowAspectRatio: tallest === AspectRatio.LANDSCAPE ? null : tallest,
            isMixed: seen.size > 1,
        };
    }, shallowEqual);
}
